import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface WhiteSmokeCloudProps {
  /** World position of the cloud centre. */
  position?: [number, number, number];
  /** 0..1 — drives overall cloud size + opacity. */
  density?: number;
  /** Number of particle instances around the core. */
  bubbleCount?: number;
  /** Spread of the cloud in metres. */
  spreadRadius?: number;
}

/**
 * White smoke cloud — three-layer cloud rendered as:
 *   1. A bright core sphere (large, pulsing, drives the main visibility)
 *   2. A few medium "puff" spheres surrounding the core (drifting outward)
 *   3. An instanced particle halo (small wispy spheres for movement)
 *   4. A point light at the centre so the cloud illuminates itself
 *
 * Uses meshBasicMaterial throughout so the cloud is visible regardless of
 * the scene's lighting (especially important in dark mode).
 */
export function WhiteSmokeCloud({
  position = [0, 0, 0],
  density = 0,
  bubbleCount = 40,
  spreadRadius = 0.1,
}: WhiteSmokeCloudProps) {
  const coreRef = useRef<THREE.Mesh>(null);
  const haloRef = useRef<THREE.Mesh>(null);
  const lightRef = useRef<THREE.PointLight>(null);
  const puffsRef = useRef<(THREE.Mesh | null)[]>([]);
  const partRef = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  // Pre-generate stable seeds for the puff spheres and instanced particles.
  const puffSeeds = useMemo(() => Array.from({ length: 5 }, () => ({
    rx: (Math.random() - 0.5) * spreadRadius * 1.6,
    ry: (Math.random() - 0.5) * spreadRadius * 0.8,
    rz: (Math.random() - 0.5) * spreadRadius * 1.6,
    phase: Math.random() * Math.PI * 2,
    baseScale: 0.6 + Math.random() * 0.5,
  })), [spreadRadius]);

  const partSeeds = useMemo(() => Array.from({ length: bubbleCount }, () => ({
    rx: (Math.random() - 0.5) * spreadRadius * 1.8,
    ry: (Math.random() - 0.5) * spreadRadius * 1.0,
    rz: (Math.random() - 0.5) * spreadRadius * 1.8,
    speed: 0.10 + Math.random() * 0.16,
    phase: Math.random(),
    size: 0.018 + Math.random() * 0.020,
    rise: 0.05 + Math.random() * 0.12,
    swayPhase: Math.random() * Math.PI * 2,
  })), [bubbleCount, spreadRadius]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    const d = Math.max(0, Math.min(1, density));

    // ---- Core sphere ----
    if (coreRef.current) {
      const pulse = 1 + 0.10 * Math.sin(t * 1.6);
      const targetSize = (spreadRadius * 0.65) * d * pulse;
      coreRef.current.scale.setScalar(targetSize);
      coreRef.current.position.set(position[0], position[1], position[2]);
      const mat = coreRef.current.material as THREE.MeshBasicMaterial;
      if (mat) mat.opacity = 0.85 * d;
    }

    // ---- Soft outer halo ----
    if (haloRef.current) {
      const wobble = 1 + 0.18 * Math.sin(t * 1.2);
      haloRef.current.scale.setScalar(spreadRadius * 1.4 * d * wobble);
      haloRef.current.position.set(position[0], position[1] + 0.02 * d, position[2]);
      const mat = haloRef.current.material as THREE.MeshBasicMaterial;
      if (mat) mat.opacity = 0.30 * d;
    }

    // ---- Puff spheres orbiting the core ----
    puffSeeds.forEach((p, i) => {
      const mesh = puffsRef.current[i];
      if (!mesh) return;
      const t2 = t * 0.7 + p.phase;
      const offsetX = p.rx + Math.sin(t2) * spreadRadius * 0.15;
      const offsetY = p.ry + Math.cos(t2 * 0.8) * spreadRadius * 0.10 + (Math.sin(t2 * 0.5) + 1) * 0.06 * d;
      const offsetZ = p.rz + Math.cos(t2 * 1.1) * spreadRadius * 0.15;
      mesh.position.set(position[0] + offsetX * d, position[1] + offsetY, position[2] + offsetZ * d);
      mesh.scale.setScalar(spreadRadius * 0.5 * p.baseScale * d);
      const mat = mesh.material as THREE.MeshBasicMaterial;
      if (mat) mat.opacity = 0.65 * d;
    });

    // ---- Wispy instanced particles drifting upward ----
    if (partRef.current) {
      const visible = Math.floor(bubbleCount * d);
      for (let i = 0; i < bubbleCount; i++) {
        if (i >= visible) {
          dummy.position.set(0, -999, 0);
          dummy.scale.setScalar(0);
          dummy.updateMatrix();
          partRef.current.setMatrixAt(i, dummy.matrix);
          continue;
        }
        const b = partSeeds[i];
        const cycle = (t * b.speed + b.phase) % 1;
        const x = position[0] + b.rx + Math.sin(t * 0.7 + b.swayPhase) * 0.012;
        const y = position[1] + b.ry + cycle * b.rise;
        const z = position[2] + b.rz + Math.cos(t * 0.6 + b.swayPhase) * 0.012;
        const lifeFade = 1 - cycle * 0.55;
        dummy.position.set(x, y, z);
        dummy.scale.setScalar(b.size * lifeFade * d);
        dummy.updateMatrix();
        partRef.current.setMatrixAt(i, dummy.matrix);
      }
      partRef.current.instanceMatrix.needsUpdate = true;
    }

    // ---- Point light at the centre so the cloud illuminates its surroundings ----
    if (lightRef.current) {
      lightRef.current.position.set(position[0], position[1], position[2]);
      lightRef.current.intensity = 0.6 * d;
    }
  });

  return (
    <group>
      {/* Bright core sphere — main visibility */}
      <mesh ref={coreRef} renderOrder={10}>
        <sphereGeometry args={[1, 18, 18]} />
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={0}
          depthWrite={false}
        />
      </mesh>

      {/* Soft outer halo */}
      <mesh ref={haloRef} renderOrder={9}>
        <sphereGeometry args={[1, 14, 14]} />
        <meshBasicMaterial
          color="#f6f6f8"
          transparent
          opacity={0}
          depthWrite={false}
        />
      </mesh>

      {/* Puff spheres */}
      {puffSeeds.map((_, i) => (
        <mesh
          key={i}
          ref={(m) => { puffsRef.current[i] = m; }}
          renderOrder={11}
        >
          <sphereGeometry args={[1, 14, 14]} />
          <meshBasicMaterial
            color="#ffffff"
            transparent
            opacity={0}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Instanced wispy particles for movement */}
      <instancedMesh
        ref={partRef}
        args={[undefined, undefined, bubbleCount]}
        renderOrder={12}
      >
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={0.9}
          depthWrite={false}
        />
      </instancedMesh>

      {/* Point light so the cloud lights up the nearby apparatus */}
      <pointLight ref={lightRef} color="#ffffff" distance={0.6} decay={2} intensity={0} />
    </group>
  );
}

export default WhiteSmokeCloud;
